import React, { useEffect, useState } from "react";
import { Application, Program, User } from "../../../core/types/types";
import { useSelector } from "react-redux";
import apiInstance from "../../../utility/apiInstance";
import { all_routes } from "../../router/all_routes";

interface PopupProps {
    show: boolean;
    onClose: () => void;
    program: Program | null;
}

const ApplyPopup: React.FC<PopupProps> = ({ show, onClose, program }) => {
    const user = useSelector((state: any) => state.auth.user);
    const isRecruiter = user?.role === 'recruiter' || user?.role === 'account_manager';
    const [students, setStudents] = useState<User[]>([]);
    const [selectedStudent, setSelectedStudent] = useState('');

    const dateOptions: Intl.DateTimeFormatOptions = {
        day: "2-digit",
        month: "short",
        year: "numeric",
    };

    const [msg, setMsg] = useState({
        error: false,
        loading: false,
        text: "",
    });

    useEffect(() => {
        if (show) {
            setMsg({ error: false, loading: false, text: "" });
            setSelectedStudent('');
            if(isRecruiter) {
                apiInstance.get('/api/users', {
                    params: {
                        'where[and][0][role][equals]': 'student',
                        'where[and][1][agent][equals]': user.id,
                    }
                }).then(res => setStudents(res.data.docs));
            }
        }
    }, [show]);

    const handlePayment = async () => {
        const applicationId = await handleApply();
        if (!applicationId) return;

        try {
            // The server looks up the fee and currency from the application itself.
            const res = await apiInstance.post('/api/create-checkout-session', {
                applicationId,
                returnUrl: `${window.location.origin}${isRecruiter ? all_routes.recruiterApplications : all_routes.studentDashboard}`,
            });
            if (res.data.success && res.data.checkoutUrl) {
                window.location.href = res.data.checkoutUrl;
                return;
            }
            setMsg({ error: true, loading: false, text: res.data.error || 'Failed to start payment' });
        } catch (err: any) {
            setMsg({ error: true, loading: false, text: err?.response?.data?.error || 'Failed to start payment' });
        }
    }

    // Returns the application id to pay for, or null if nothing should be paid.
    const handleApply = async (): Promise<string | null> => {
        setMsg({
            error: false,
            loading: true,
            text: "",
        });

        const studentId = isRecruiter ? selectedStudent : user.id;

        if(isRecruiter && !studentId) {
            setMsg({
                error: true,
                loading: false,
                text: "Please select a student."
            })
            return null;
        }

        try {
            const existing = await apiInstance.get("/api/applications", {
                params: {
                    "where[student][equals]": studentId,
                    "where[program][equals]": program?.id,
                },
            });

            if (existing.data.docs.length > 0) {
                const existingApp = existing.data.docs[0];
                setMsg({
                    error: true,
                    loading: false,
                    text: "You have already applied for this program!",
                });
                return existingApp.isApplicationFeePaid ? null : existingApp.id;
            }

            const newApp: Partial<Application> = {
                status: "pending",
                student: studentId,
                program: program?.id,
                institution: program?.institution.id,
                applicationFee: program?.applicationFee,
            };

            const res = await apiInstance.post("/api/applications", newApp);
            setMsg({
                error: false,
                loading: false,
                text: "Application created successfully!",
            });

            setTimeout(onClose, 3000);
            return res.data.doc.id;
        } catch (err: any) {
            const responseErrors: { message: string; data?: { errors?: { label: string; message: string }[] } }[] =
                err?.response?.data?.errors;
            let errorText = err.message;
            if (responseErrors?.length) {
                errorText = responseErrors
                    .flatMap((e) => e.data?.errors?.map((fe) => `${fe.label}: ${fe.message}`) ?? [e.message])
                    .join(", ");
            }
            setMsg({
                error: true,
                loading: false,
                text: errorText,
            });
            return null;
        }
    };

    const applicant = isRecruiter ? students?.find(st => st.id === selectedStudent) : user;

    return (
        <div
            className={`modal fade ${show ? "show d-block" : ""}`}
            tabIndex={-1}
            role="dialog"
            style={{ background: show ? "rgba(0,0,0,0.5)" : "transparent" }}
        >
            <div className="modal-dialog-centered w-50 m-auto" role="document">
                <div className="modal-content">
                    {/* Header */}
                    <div className="modal-header">
                        <h5 className="modal-title text-secondary">Program Application</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                        ></button>
                    </div>

                    {/* Body */}
                    <div className="modal-body">
                        <h5 className="fw-bold mb-5 text-center">{program?.programName}</h5>

                        {isRecruiter && (
                            <div className="row mb-2">
                                <div className="col-sm-4 fw-semibold text-muted">Select Student:</div>
                                <div className="col-sm-8">
                                    <select className="form-select form-select-sm"
                                        value={selectedStudent}
                                        onChange={e => setSelectedStudent(e.target.value)}>
                                        <option value="">-- Select a student --</option>
                                        {students.map(s => (
                                            <option key={s.id} value={s.id}>
                                                {s.firstName} {s.lastName} ({s.email})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}

                        <div className="course-page-content pt-0">
                            <div className="card mb-4 shadow-sm border-0 rounded-3">
                                <div className="card-body">

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">Applicant:</div>
                                        <div className="col-sm-8">
                                            {applicant?.firstName || ''} {applicant?.lastName || ''}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">Program:</div>
                                        <div className="col-sm-8">
                                            {program?.programName || ''}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">Institution:</div>
                                        <div className="col-sm-8">
                                            {program?.institution?.schoolName || ''}
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted">Application Date:</div>
                                        <div className="col-sm-8">
                                            {new Date().toLocaleDateString(
                                                "en-IN",
                                                dateOptions
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted"> Program Start Date:</div>
                                        <div className="col-sm-8">
                                            {program?.start
                                                ? new Date(program.start).toLocaleDateString(
                                                    "en-IN",
                                                    dateOptions
                                                )
                                                : "-"}
                                        </div>
                                    </div>  
                                    <div className="row mb-2">
                                        <div className="col-sm-4 fw-semibold text-muted"> Program End Date:</div>
                                        <div className="col-sm-8">
                                            {program?.end
                                                ? new Date(program.end).toLocaleDateString(
                                                    "en-IN",
                                                    dateOptions
                                                )
                                                : "-"}
                                        </div>
                                    </div>                                                                        
                                </div>
                            </div>
                        </div>

                        <div className="course-page-content pt-0">
                            <div className="card mb-4">
                                <div className="card-body">
                                    <h5 className="mb-3 text-primary">Program Details</h5>
                                    {program?.about || ""}
                                </div>
                            </div>
                        </div>
                        {program?.requirement && (
                            <div className="course-page-content pt-0">
                                <div className="card mb-4">
                                    <div className="card-body">
                                        <h5 className="mb-3 text-primary">Program Requirements</h5>
                                        {program?.requirement || ""}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="modal-footer d-flex gap-3">
                        {msg.loading && (
                            <span className="mt-3 fs-14 fw-normal d-flex align-items-center justify-content-center">
                                Creating application...
                            </span>
                        )}
                        {!msg.loading && (
                            <span
                                className={`mt-3 ${msg.error ? "text-red" : ""} fs-14 fw-normal d-flex align-items-center justify-content-center`}
                            >
                                {msg.text}
                            </span>
                        )}
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleApply}
                            disabled={msg.loading}
                        >
                            Apply
                        </button>

                        {(program?.applicationFee || 0) > 0 && (
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handlePayment}
                                disabled={msg.loading}
                            >
                                Apply &amp; Pay Now
                            </button>
                        )}

                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApplyPopup;
